/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Divider } from '@wiggui/ds/Divider'
import { MONOPOLIO_PROPERTY_TYPES, OPERATION_OPTIONS } from '@/utils/constants'
import { PriceSelect } from '@/components'
import { useFilters } from '@/hooks/useFilters'
import { MoreFiltersModal } from './modal'
import { useTourSearchWizzard } from './tour'
import { useAmplitude } from '@wiggot/analytics/src/providers/amplitud-provider'
import { useEventSearch } from '@wiggot/analytics/src/providers/posthog-provider/hooks/use-event-search'
import { AmplitudeEvents } from '@/interfaces/enum/amplitudeEvents.enum'
import { OperationTypeDropdow } from '@/components/Dropdows/OperationTypeDropdow'
import { SearchButton } from '@/components/Dropdows/SearchButton'
import { PropertyTypeDropdown } from '@/components/Dropdows/PropertyTypeDropdown'
import { MainFiltersModal } from './modal/main-filters/MainFiltersModal'
import { numberWithCommaWithoutDecimals } from '@wiggot/utils/src/formatters'
import GoogleIcon from '@wiggui/ds/GoogleIcon'
import { PillButton } from '@wiggui/ds/PillButton'
import { OptionButton } from '@wiggui/ds/OptionButton'
import { Transition } from '@wiggui/ds/Transition'
import { Portal } from '@wiggui/ds/Portal'
import { Skeleton } from '@wiggui/ds/Skeleton'
import { SearchPropertyById } from './SearchPropertyById'
import useMatchWindowQuery from '@wiggot/web-components/hooks/ds/useMatchWindowQuery'

const DROPDOWN_INITIAL_STATE = {
  isOpenOperationType: false,
  isOpenPropertyType: false,
  isOpenSearch: false,
  isOpenPrice: false
}
export type DropdownKeys = keyof typeof DROPDOWN_INITIAL_STATE
interface IProps {
  animateSearchBar: boolean
  setShowFirtsSearch: (show: boolean) => void
  hasCleanFilters: boolean
  setHasCleanFilters: (hasCleanFilters: boolean) => void
}
export const SelectFilters = ({
  setShowFirtsSearch,
  animateSearchBar,
  hasCleanFilters,
  setHasCleanFilters
}: IProps) => {
  const { searchStartAction } = useEventSearch()
  const { trackEvent, isInitialized } = useAmplitude()
  const { isMD } = useMatchWindowQuery()
  const [isOpen, setIsOpen] = useState(false)
  const [isOpenMainFilters, setIsOpenMainFilters] = useState(false)
  const [isOpenDropdows, setIsOpenDropdows] = useState(DROPDOWN_INITIAL_STATE)
  const {
    filters,
    setFilters,
    onChangeOperationType,
    onChangePrices,
    onInputPricesChange,
    onConfirm,
    associations,
    moreFiltersCounter,
    searchAddress,
    isLoadingPreviousFilters,
    applyMoreFilters,
    resetMoreFilters,
    onChangePropertyType,
    onApplyPropertyTypes,
    handleOpportunity,
    onSearchFilters,
    setAutocompleteValue,
    autocompleteValue,
    onChangeAssociations,
    onApplyAssociations,
    onChangeLocation,
    onDeleteLocation
  } = useFilters({
    setShowFirtsSearch,
    hasCleanFilters,
    setHasCleanFilters
  })

  const prices = filters?.prices
  const propertyTypes = filters.property_type
  const { initWizzard: initTourSearchWizzard } = useTourSearchWizzard()
  const mainFilters = useRef<HTMLDivElement>(null)
  const onChangeDropdownOpen = ({
    isOpen,
    dropdownKey
  }: {
    isOpen: boolean
    dropdownKey: DropdownKeys
  }) => {
    if (isLoadingPreviousFilters) {
      return
    }

    if (isOpen && dropdownKey === 'isOpenSearch') {
      setAutocompleteValue(' ')
    }
    if (isOpen) {
      const newDropdows = { ...DROPDOWN_INITIAL_STATE }
      newDropdows[dropdownKey] = true
      setIsOpenDropdows(newDropdows)
      return
    }

    setIsOpenDropdows(DROPDOWN_INITIAL_STATE)
  }
  const isSomeDropdownOpen = Object.entries(isOpenDropdows).find(
    ([, isOpen]) => isOpen
  )
  const onClickSearch = () => {
    searchStartAction()
    setIsOpenDropdows(DROPDOWN_INITIAL_STATE)
    onSearchFilters()
  }
  const operationTypeData = OPERATION_OPTIONS.find(
    (option) => option.value === filters.operation_type[0]
  )
  const pricesToShowCopy = useMemo(() => {
    if (
      (!prices?.priceFrom || prices?.priceFrom < 2) &&
      (!prices?.priceTo || prices?.priceTo < 2)
    ) {
      return '¿Cuál es el presupuesto?'
    }

    if (!prices?.priceFrom || prices?.priceFrom < 2) {
      return `Hasta ${numberWithCommaWithoutDecimals(
        prices?.priceTo,
        true
      )} MXN`
    }

    if (!prices?.priceTo || prices?.priceTo < 2) {
      return `Desde ${numberWithCommaWithoutDecimals(
        prices?.priceFrom,
        true
      )} MXN`
    }

    return `${numberWithCommaWithoutDecimals(
      prices?.priceFrom,
      true
    )} - ${numberWithCommaWithoutDecimals(prices?.priceTo, true)} MXN`
  }, [prices?.priceFrom, prices?.priceTo])

  const propertyTypeCopy = useMemo(() => {
    if (!propertyTypes || propertyTypes.length === 0) {
      return 'Agrega un tipo de propiedad'
    }
    const typesText: string[] = []
    MONOPOLIO_PROPERTY_TYPES.forEach((option) => {
      if (propertyTypes.includes(option.value)) {
        typesText.push(option.label)
      }
    })
    const concatenatedText = typesText.join(', ')
    if (concatenatedText.length > 27) {
      const smallText = typesText.slice(0, 2).join(', ')
      return `${smallText}, +${typesText.slice(2).length}...`
    }
    return concatenatedText
  }, [propertyTypes])

  useEffect(() => {
    if (animateSearchBar && isMD) {
      const isSomeDropdownOpen = Object.entries(isOpenDropdows).find(
        ([, isOpen]) => isOpen
      )
      if (!isSomeDropdownOpen) {
        setTimeout(() => {
          onChangeDropdownOpen({
            isOpen: true,
            dropdownKey: 'isOpenOperationType'
          })
        }, 500)
      }
    }
  }, [animateSearchBar])

  return (
    <div className="h-full gap-2 flex items-center justify-between sm:justify-start z-10 w-full overflow-x-auto lg:overflow-visible">
      {animateSearchBar && (
        <Portal className="flex flex-row justify-center items-center fixed top-0 left-0 w-full h-full z-10">
          <Transition show={animateSearchBar}>
            <div
              className=" bg-white"
              style={{
                backdropFilter: 'brightness(1.2)',
                opacity: 0.1,
                left: '0px',
                top: '0px',
                position: 'fixed',
                zIndex: 99999,
                pointerEvents: 'none'
                // color: 'rgb(34, 34, 34)'
              }}
            >
              <div className="w-screen h-screen">
                {/* <div className="w-[500px] h-[82px] bg-white backdrop-blur-0 rounded-full"></div> */}
              </div>
            </div>
          </Transition>
        </Portal>
      )}
      <div
        ref={mainFilters}
        style={{
          boxShadow: animateSearchBar ? '0px 4px 4px rgba(0, 0, 0, 0.05)' : ''
        }}
        className={`relative w-fit hidden md:flex pr-1 items-center gap-1 rounded-full border border-surface-strokes transition-all tour-search-step-1 ${
          isSomeDropdownOpen ? 'bg-surface-gray-10' : ''
        }`}
      >
        <Transition
          className="absolute left-0 top-0 h-full w-full backdrop-blur-0 rounded-full z-10 backdrop-brightness-50"
          show={animateSearchBar}
        >
          <div />
        </Transition>
        <OperationTypeDropdow
          isOpen={isOpenDropdows.isOpenOperationType}
          operationTypeData={operationTypeData}
          onChangeOperationType={onChangeOperationType}
          setIsOpen={onChangeDropdownOpen}
          isLoading={isLoadingPreviousFilters}
        />
        <Divider isVertical={false} height="24px" width="1px" />
        <SearchButton
          isOpen={isOpenDropdows.isOpenSearch}
          setIsOpen={onChangeDropdownOpen}
          onChangeLocation={onChangeLocation}
          autocompleteValue={autocompleteValue}
          isLoading={isLoadingPreviousFilters}
          locations={filters?.locations}
          onDeleteLocation={onDeleteLocation}
        />
        <Divider isVertical={false} height="24px" width="1px" />
        <PriceSelect
          isOpen={isOpenDropdows.isOpenPrice}
          setIsOpen={onChangeDropdownOpen}
          setPrices={onChangePrices}
          onInputPricesChange={onInputPricesChange}
          prices={filters?.prices}
          pricesToShowCopy={pricesToShowCopy}
          isLoading={isLoadingPreviousFilters}
        />
        <Divider isVertical={false} height="24px" width="1px" />
        <PropertyTypeDropdown
          isOpen={isOpenDropdows.isOpenPropertyType}
          onChangePropertyType={onChangePropertyType}
          propertyTypes={propertyTypes}
          setIsOpen={onChangeDropdownOpen}
          propertyTypeCopy={propertyTypeCopy}
          isLoading={isLoadingPreviousFilters}
        />
        <button
          type="button"
          onClick={onClickSearch}
          className="flex p-3 w-11 h-11 justify-center items-center rounded-full bg-button-primary hover:bg-button-primary-hover active:bg-button-primary-pressed"
        >
          <GoogleIcon className="text-xl text-white font-bold" name="search" />
        </button>
      </div>
      <button
        type="button"
        disabled={isLoadingPreviousFilters}
        onClick={() => setIsOpenMainFilters(true)}
        className="relative h-[52px] flex md:hidden items-center gap-2 border border-surface-strokes rounded-full px-4 py-[6px] w-full max-w-[55%] tour-search-step-mb-1"
      >
        <Transition
          className="absolute left-0 top-0 h-full w-full backdrop-blur-0 rounded-full z-10 backdrop-brightness-50"
          show={animateSearchBar}
        >
          <div />
        </Transition>
        <GoogleIcon name="search" />

        <article className="flex flex-col items-start gap-1 max-w-[100%]">
          <p className="text-secondary text-sm whitespace-nowrap">
            Realizar búsqueda
          </p>
          {isLoadingPreviousFilters ? (
            <Skeleton
              className="truncate"
              width="w-[200px] max-w-[80%]"
              height="h-6"
            />
          ) : (
            <p className="text-base truncate max-w-[80%]">
              {operationTypeData?.label} •{' '}
              {searchAddress ? searchAddress : 'Dirección'} • Precio • Casa •
              Apártamento
            </p>
          )}
        </article>
      </button>
      <Divider
        className="min-w-[1px]"
        isVertical={false}
        height="42px"
        width="1px"
      />
      <SearchPropertyById />
      <div className="flex flex-1 items-center justify-between w-1/4">
        <div className="flex flex-row gap-2 relative">
          <PillButton
            onClick={() => setIsOpen(true)}
            className="h-[42px] w-[42px] 2xl:w-auto tour-search-step-2"
          >
            <p className="hidden 2xl:block">Filtros</p>
            <GoogleIcon
              name="tune"
              className="2xl:ml-2 min-w-[16px] min-h-[16px] text-base font-semibold"
            />
          </PillButton>
          {moreFiltersCounter > 0 && (
            <div className="absolute min-w-[24px] min-h-[24px] text-center rounded-full px-2 py-1 bg-surface-bg-black text-white text-[0.625rem] font-bold right-0 -top-2">
              {moreFiltersCounter}
            </div>
          )}
        </div>
        <OptionButton
          className="tour-search-step-6"
          onClick={() => {
            initTourSearchWizzard(true)
            if (isInitialized) {
              trackEvent({
                name: AmplitudeEvents.SEARCH_RESULTS_WIZZARD_VIEW
              })
            }
          }}
          onlyIcon={
            <GoogleIcon
              name="help"
              variant="outlined"
              className="text-primary text-[24px]"
            />
          }
        />
      </div>
      {isOpen && (
        <MoreFiltersModal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          {...{
            filters,
            setFilters,
            applyMoreFilters,
            resetMoreFilters,
            onChangeOperationType,
            onChangePrices,
            onConfirm,
            associations,
            onChangePropertyType,
            onApplyPropertyTypes,
            handleOpportunity,
            onChangeAssociations,
            onApplyAssociations,
            OPERATION_OPTIONS
          }}
        />
      )}
      <Transition
        animationEnd="slideOutUp"
        delay={0}
        duration={300}
        show={isOpenMainFilters}
      >
        <MainFiltersModal
          setAutocompleteValue={setAutocompleteValue}
          locations={filters?.locations}
          onDeleteLocation={onDeleteLocation}
          isOpen={isOpenMainFilters}
          operationTypeData={operationTypeData}
          onClose={() => setIsOpenMainFilters(false)}
          onClickSearch={onClickSearch}
          pricesToShowCopy={pricesToShowCopy}
          propertyTypeCopy={propertyTypeCopy}
          onChangeOperationType={onChangeOperationType}
          onInputPricesChange={onInputPricesChange}
          onChangePropertyType={onChangePropertyType}
          onChangeLocation={onChangeLocation}
          propertyTypes={propertyTypes}
          prices={prices}
          autocompleteValue={autocompleteValue}
        />
      </Transition>
    </div>
  )
}
